import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, MessagesSquare, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

/**
 * Admin view of chat sessions — see what prospective users are asking
 * so you can prune docs / product friction.
 */
export default function AdminChats() {
    const [sessions, setSessions]   = useState([]);
    const [totals, setTotals]       = useState({ total_sessions: 0, total_messages: 0 });
    const [loading, setLoading]     = useState(false);
    const [transcript, setTranscript] = useState(null);   // { session_id, messages }
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const [bustingKb, setBustingKb] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/chats?limit=100");
            setSessions(data.sessions || []);
            setTotals({
                total_sessions: data.total_sessions || 0,
                total_messages: data.total_messages || 0,
            });
        } catch {
            toast.error("Failed to load chats");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openTranscript = async (sid) => {
        setTranscript({ session_id: sid, messages: [] });
        setTranscriptLoading(true);
        try {
            const { data } = await api.get(`/admin/chats/${encodeURIComponent(sid)}`);
            setTranscript(data);
        } catch {
            toast.error("Failed to load transcript");
            setTranscript(null);
        } finally {
            setTranscriptLoading(false);
        }
    };

    const bustKb = async () => {
        setBustingKb(true);
        try {
            await api.post("/admin/chat/kb/refresh");
            toast.success("Knowledge base cache cleared — next chat message will re-read the docs.");
        } catch {
            toast.error("KB refresh failed");
        } finally {
            setBustingKb(false);
        }
    };

    const chatWidgets = [
        {
            id: "chat-sessions-list", title: "Chat sessions", category: "Support", layout: { x: 0, y: 0, w: 12, h: 14 }, minW: 6, minH: 6,
            render: () => (
                loading && sessions.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
                ) : sessions.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-lg"
                         data-testid="admin-chats-empty">
                        No chat sessions yet.
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="admin-chats-list">
                        {sessions.map((s) => (
                            <button
                                key={s.session_id}
                                onClick={() => openTranscript(s.session_id)}
                                className="w-full text-left rounded-xl border border-border bg-card hover:bg-muted/40 transition-colors p-4"
                                data-testid={`admin-chats-row-${s.session_id}`}
                            >
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <MessagesSquare className="w-3.5 h-3.5 text-primary" />
                                    <span className="text-xs font-mono text-muted-foreground">{s.session_id}</span>
                                    {s.user_id ? (
                                        <span className="text-[10px] uppercase tracking-widest font-bold bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 rounded">
                                            Signed-in
                                        </span>
                                    ) : (
                                        <span className="text-[10px] uppercase tracking-widest font-bold bg-muted text-muted-foreground border border-border px-2 py-0.5 rounded">
                                            Anonymous
                                        </span>
                                    )}
                                    <span className="text-[10px] uppercase tracking-widest font-bold bg-secondary text-foreground border border-border px-2 py-0.5 rounded">
                                        {s.turn_count} turn{s.turn_count === 1 ? "" : "s"}
                                    </span>
                                    <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{timeAgo(s.last_ts)}</span>
                                </div>
                                {s.first_user_msg && (
                                    <div className="text-sm mb-1">
                                        <span className="text-muted-foreground">Opened with: </span>
                                        <span className="text-foreground">"{s.first_user_msg}"</span>
                                    </div>
                                )}
                                {s.last_ai_msg && (
                                    <div className="text-xs text-muted-foreground line-clamp-2 italic">
                                        Kimani: {s.last_ai_msg}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                )
            ),
        },
    ];

    return (
        <div className="space-y-6" data-testid="admin-chats-page">
            <PageHeader
                icon={MessagesSquare}
                eyebrow="Admin Tools"
                title="Chats"
                subtitle={`${totals.total_sessions} sessions · ${totals.total_messages} messages`}
                actions={
                    <>
                        <Button variant="outline" size="sm" onClick={bustKb} disabled={bustingKb}
                                className="gap-1" data-testid="admin-chats-refresh-kb">
                            <Sparkles size={14} className={bustingKb ? "animate-pulse" : ""} />
                            {bustingKb ? "Refreshing…" : "Refresh KB"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={load}
                                data-testid="admin-chats-refresh">
                            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
                            Reload
                        </Button>
                    </>
                }
                testId="admin-chats-header"
            />

            <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-chats" widgets={chatWidgets} />

            {transcript && (
                <TranscriptDrawer
                    data={transcript}
                    loading={transcriptLoading}
                    onClose={() => setTranscript(null)}
                />
            )}
        </div>
    );
}

function TranscriptDrawer({ data, loading, onClose }) {
    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-[80]" onClick={onClose}
                 data-testid="admin-chats-transcript-overlay" />
            <aside
                className="fixed top-0 right-0 z-[81] h-screen w-full sm:w-[480px] bg-card border-l border-border shadow-2xl overflow-y-auto"
                role="dialog"
                aria-label="Chat transcript"
                data-testid="admin-chats-transcript"
            >
                <div className="p-5 border-b border-border flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="overline text-primary">Transcript</div>
                        <div className="text-sm font-extrabold tracking-tight truncate font-mono">
                            {data.session_id}
                        </div>
                        {data.user_id && (
                            <div className="text-xs text-muted-foreground truncate">user_id: {data.user_id}</div>
                        )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}
                            data-testid="admin-chats-transcript-close">
                        <X size={16} />
                    </Button>
                </div>
                <div className="p-5 space-y-3">
                    {loading ? (
                        <div className="text-sm text-muted-foreground">Loading transcript…</div>
                    ) : (data.messages || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">No messages.</div>
                    ) : (
                        data.messages.map((m, i) => (
                            <div key={m.id || `${m.role}-${m.ts || i}`}
                                 className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                 data-testid={`admin-chats-msg-${m.role}-${i}`}>
                                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "bg-primary text-primary-foreground rounded-br-sm"
                                        : "bg-muted text-foreground rounded-bl-sm"
                                }`}>
                                    {m.text}
                                    <div className={`mt-1 text-[10px] ${m.role === "user" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                                        {timeAgo(m.ts)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </aside>
        </>
    );
}
